import { Link } from "react-router-dom";
import { Star, ArrowUpRight, CheckCircle, Camera } from "@phosphor-icons/react";
import { trackBuyClick } from "@/lib/track";

export function Rating({ value }) {
  const rounded = Math.round(value);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={13}
          weight={i <= rounded ? "fill" : "regular"}
          className={i <= rounded ? "text-[#D17A58]" : "text-[#E5E0D8]"}
        />
      ))}
    </div>
  );
}

export function BenefitList({ items, testid }) {
  if (!items?.length) return null;
  return (
    <ul data-testid={testid} className="mt-3 space-y-1.5">
      {items.slice(0, 3).map((b) => (
        <li key={b} className="flex items-start gap-2 text-sm text-[#2C2B29]">
          <CheckCircle size={16} weight="fill" className="text-[#5B7B53] mt-0.5 shrink-0" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

export function BuyButton({ product, testid, className = "" }) {
  return (
    <a
      data-testid={testid}
      href={product.affiliate_url}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      onClick={() => trackBuyClick(product.id)}
      className={`hh-btn-primary inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold whitespace-nowrap ${className}`}
    >
      Buy Now on Amazon <ArrowUpRight size={14} weight="bold" />
    </a>
  );
}

export function ProductCard({ product, rank }) {
  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="hh-card rounded-2xl overflow-hidden flex flex-col w-full"
    >
      <Link to={`/product/${product.id}`} className="block relative aspect-[4/3] overflow-hidden bg-[#EADDC8]/30">
        {rank ? (
          <div className="absolute top-3 left-3 z-10 bg-[#2C2B29] text-white text-xs font-medium px-2.5 py-1 rounded-full">
            #{rank}
          </div>
        ) : null}
        <img
          src={product.image_url}
          alt={product.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        />
        {product.gallery?.length ? (
          <span data-testid={`photo-count-${product.id}`} className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-[#2C2B29]/80 text-white text-[11px] font-semibold px-2 py-1 rounded-full">
            <Camera size={12} weight="fill" /> {new Set([product.image_url, ...product.gallery]).size} photos
          </span>
        ) : null}
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <span className="text-xs uppercase tracking-wider text-[#5B7B53] mb-2">{product.category}</span>
        <Link to={`/product/${product.id}`} className="block">
          <h3 className="font-display text-xl leading-snug text-[#2C2B29] hover:text-[#5B7B53] transition-colors">
            {product.title}
          </h3>
        </Link>
        <p className="text-sm text-[#5C5A56] mt-2 line-clamp-2">{product.short_description}</p>
        <BenefitList items={product.benefits} testid={`benefits-${product.id}`} />
        <div className="flex items-center gap-2 mt-3">
          <Rating value={product.rating} />
          <span className="text-xs text-[#5C5A56]">
            {product.rating} · {product.review_count.toLocaleString()} reviews
          </span>
        </div>
        <div className="mt-auto pt-5 space-y-3">
          <span data-testid={`price-${product.id}`} className="block text-lg font-semibold text-[#2C2B29]">{product.price || "—"}</span>
          <BuyButton product={product} testid={`buy-link-${product.id}`} className="w-full" />
        </div>
      </div>
    </article>
  );
}
